<script lang="ts" setup>
import { type MusicRank, type MusicCategory } from '~/services/musicService';
import { getSectionById } from '~/services/musicSectionMapConfig';
import { useI18n, useRouter, ref, onMounted, watch } from '#imports';
import { useSeoMeta, useHead } from '#imports';
import SearchBox from '~/components/music/SearchBox.vue';
import CloudflareImg from '~/components/base/CloudflareImg.vue';
import { useMusicCache } from '~/composables/useMusicCache';

const { t, locale } = useI18n();
const router = useRouter();
const musicCache = useMusicCache();
const { isMobile } = useDevice();

// Carry public SSR data in the Nuxt payload. Hydration must keep the rendered
// lists instead of replacing them with skeletons and fetching them again.
const initialHomeData = useState<{
  locale: string;
  trending: MusicRank[];
  categories: MusicCategory[];
  expiresAt: number;
} | null>('home-music-data', () => null);
const hasInitialData = initialHomeData.value?.locale === locale.value && initialHomeData.value.expiresAt > Date.now();
const musicTrending = ref<MusicRank[]>(hasInitialData ? initialHomeData.value!.trending : []);
const musicCategories = ref<MusicCategory[]>(hasInitialData ? initialHomeData.value!.categories : []);
const isLoadingTrending = ref(!hasInitialData);
const isLoadingCategories = ref(!hasInitialData);
let dataRequest = 0;

// 添加图片加载状态跟踪
const shouldLoadCategoryImages = ref<{[key: string]: boolean}>({});

// 播放相关功能
const currentPlayingId = ref<string | null>(null);
const isPlaying = ref(false);
const error = ref<string | null>(null);

// 跟踪图片是否应该加载
const shouldLoadImages = ref<{[key: string]: boolean}>({});
function initializeImageVisibility() {
  shouldLoadImages.value = Object.fromEntries(musicTrending.value.map((rank, index) => [rank.id, index === 0]));
  shouldLoadCategoryImages.value = Object.fromEntries(musicCategories.value.map((category, index) => [category.id, index < 4 || !isMobile]));
}
if (hasInitialData) initializeImageVisibility();

// 检查是否正在播放特定音乐
const isCurrentlyPlaying = (id: string) => {
  return currentPlayingId.value === id && isPlaying.value;
};

// 播放音乐
const playMusic = (music: any) => {
  // 如果点击当前播放的音乐，则切换播放状态
  if (currentPlayingId.value === music.id) {
    togglePlayState();
  } else {
    // 否则播放新的音乐
    currentPlayingId.value = music.id;
    isPlaying.value = true;

    // 触发全局事件，通知音乐播放器组件
    const event = new CustomEvent('play-music', {
      detail: music
    });
    window.dispatchEvent(event);
  }
};

// 切换播放状态
const togglePlayState = () => {
  isPlaying.value = !isPlaying.value;

  // 触发全局事件，通知音乐播放器组件
  const event = new CustomEvent('toggle-play-state', {
    detail: {
      id: currentPlayingId.value,
      isPlaying: isPlaying.value
    }
  });
  window.dispatchEvent(event);
};

// 使用内存缓存获取音乐数据
const loadData = async () => {
  const request = ++dataRequest;
  const requestLocale = locale.value;
  try {
    isLoadingTrending.value = true;
    isLoadingCategories.value = true;

    // 并行获取数据
    const [trendingResponse, categoriesResponse] = await Promise.allSettled([
      musicCache.getFeaturedMusic(),
      musicCache.getCategoryList()
    ]);
    if (request !== dataRequest || requestLocale !== locale.value) return;

    // 处理榜单数据
    if (trendingResponse.status === 'fulfilled' && trendingResponse.value && trendingResponse.value.data) {
      musicTrending.value = Array.isArray(trendingResponse.value.data)
        ? trendingResponse.value.data
        : (trendingResponse.value.data.list || []);

      // 初始化榜单可见性状态，第一个榜单默认可见
      if (musicTrending.value.length > 0) {
        musicTrending.value.forEach((trending, index) => {
          shouldLoadImages.value[trending.id] = index === 0; // 只有第一个榜单默认加载图片
        });
      }
    } else if (trendingResponse.status === 'rejected') {
      console.error('加载榜单数据失败:', trendingResponse.reason);
      if (!import.meta.server) {
        error.value = '加载榜单数据失败，请稍后再试';
      }
      // 设置空数组防止页面报错
      musicTrending.value = [];
    }

    // 处理分类数据
    if (categoriesResponse.status === 'fulfilled' && categoriesResponse.value && categoriesResponse.value.data) {
      musicCategories.value = Array.isArray(categoriesResponse.value.data)
        ? categoriesResponse.value.data
        : (categoriesResponse.value.data.list || []);
            // 初始化分类图片加载状态，只对前4个分类设置为true
      if (musicCategories.value.length > 0) {
        /// 此处如果是在手机端，就展示4个分类封面，在PC就全部展示
        musicCategories.value.forEach((category, index) => {
          shouldLoadCategoryImages.value[category.id] = index < 4 || !isMobile;
        });
      }
    } else if (categoriesResponse.status === 'rejected') {
      console.error('加载分类数据失败:', categoriesResponse.reason);
      if (!import.meta.server) {
        error.value = error.value || '加载分类数据失败，请稍后再试';
      }
      // 设置空数组防止页面报错
      musicCategories.value = [];
    }
    if (trendingResponse.status === 'fulfilled' && categoriesResponse.status === 'fulfilled' && musicTrending.value.length && musicCategories.value.length) {
      initialHomeData.value = { locale: requestLocale, trending: musicTrending.value, categories: musicCategories.value, expiresAt: Date.now() + 3600000 };
    }
  } catch (error) {
    if (request !== dataRequest) return;
    console.error('加载数据失败:', error);
    // 设置空数组防止页面报错
    musicTrending.value = [];
    musicCategories.value = [];
  } finally {
    if (request === dataRequest) {
      isLoadingTrending.value = import.meta.server && musicTrending.value.length === 0;
      isLoadingCategories.value = import.meta.server && musicCategories.value.length === 0;
    }
  }
};

// SSR 阶段等待数据，确保首次渲染有内容；
// 客户端 SPA 导航时非阻塞，立即跳转，shimmer 兜底
if (import.meta.server) {
  try {
    await loadData();
  } catch (e) {
    console.error('初始化数据加载失败:', e);
  }
} else if (!hasInitialData) {
  loadData(); // 不 await，导航立即完成
}

// 添加重试加载数据的功能
const retryLoadData = () => {
  error.value = null;
  loadData();
};

// 监听语言变化，重新加载数据
watch(locale, () => {
  loadData();
});

// 跳转到详情页
const navigateToMusicDetail = (music: any) => {
  // 简单地导航到详情页，只传递ID
  router.push(`/music/song/${music.id}`);
};
// 监听全局播放状态变化和初始化交叉观察器
onMounted(() => {
  // 监听播放状态变化
  window.addEventListener('playing-state-changed', ((e: CustomEvent) => {
    const { id, isPlaying: newIsPlaying } = e.detail;
    currentPlayingId.value = id;
    isPlaying.value = newIsPlaying;
  }) as EventListener);

  // 初始化统一的交叉观察器
  if (typeof IntersectionObserver !== 'undefined') {
    const observerConfig = {
      threshold: 0.1,
      rootMargin: '50px'
    };

    // 统一的观察器回调
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        const target = entry.target as HTMLElement;
        if (!entry.isIntersecting) return;

        // 处理榜单图片加载
        if (target.dataset.rankingId) {
          shouldLoadImages.value[target.dataset.rankingId] = true;
        }

        // 处理分类图片加载
        if (target.dataset.categoryId) {
          shouldLoadCategoryImages.value[target.dataset.categoryId] = true;
        }
      });
    };

    // 创建统一的观察器
    const observer = new IntersectionObserver(handleIntersection, observerConfig);

    // 监听所有卡片
    setTimeout(() => {
      document.querySelectorAll('.ranking-card, .category-card').forEach(card => {
        observer.observe(card);
      });
    }, 100);
  }
});

// SEO配置 - 使用新的composable
const { setHomePageSeo } = useSeo();
setHomePageSeo();

// FAQPage 结构化数据
const faqSchema = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: t('music.faqList.q1'),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t('music.faqList.a1')
      }
    },
    {
      '@type': 'Question',
      name: t('music.faqList.q2'),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t('music.faqList.a2')
      }
    },
    {
      '@type': 'Question',
      name: t('music.faqList.q3'),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t('music.faqList.a3')
      }
    }
  ]
}));

useHead({
  script: [
    {
      type: 'application/ld+json',
      children: () => JSON.stringify(faqSchema.value)
    }
  ]
});

const searchQuery = ref('');

// FAQ 交互状态
const activeFaqIndex = ref<number | null>(null);
const toggleFaq = (index: number) => {
  activeFaqIndex.value = activeFaqIndex.value === index ? null : index;
};
</script>

<template>
  <div class="h-auto text-gray-100 relative overflow-hidden">
    <!-- 主容器 -->
    <div class="lg:ml-4 lg:pr-4 relative">
      <!-- 页面顶部合适的位置添加搜索框 -->
      <div class="w-full max-w-3xl mx-auto mb-4 md:mb-8">
        <SearchBox
          v-model="searchQuery"
          :show-cancel="false"
          @click="router.push('/music/search')"
        />
      </div>

      <!-- 移动端金刚位导航 -->
      <div class="grid grid-cols-4 gap-2 mb-6 md:hidden">
        <!-- 音乐生成 -->
        <div
          class="flex flex-col items-center justify-center cursor-pointer gap-1"
          @click="router.push('/create')"
        >
          <div class="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-purple-600 dark:text-white">
            <div class="i-lucide-music text-2xl"></div>
          </div>
          <span class="text-xs text-gray-300">{{ t('nav.createMusicShort') }}</span>
        </div>

        <!-- 歌词生成 -->
        <div
          class="flex flex-col items-center justify-center cursor-pointer gap-1"
          @click="router.push('/lyrics-generator')"
        >
          <div class="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-purple-600 dark:text-white">
            <div class="i-lucide-file-text text-2xl"></div>
          </div>
          <span class="text-xs text-gray-300">{{ t('nav.lyricGeneratorShort') }}</span>
        </div>

        <!-- 音乐库 -->
        <div
          class="flex flex-col items-center justify-center cursor-pointer gap-1"
          @click="router.push('/library')"
        >
          <div class="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-purple-600 dark:text-white">
            <div class="i-lucide-library text-2xl"></div>
          </div>
          <span class="text-xs text-gray-300">{{ t('nav.library') }}</span>
        </div>

        <!-- 其他 -->
        <div
          class="flex flex-col items-center justify-center cursor-pointer gap-1"
          @click="router.push('/m-create')"
        >
          <div class="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-purple-600 dark:text-white">
            <div class="i-lucide-grid-3x3 text-2xl"></div>
          </div>
          <span class="text-xs text-gray-300">{{ t('common.more') || '其他' }}</span>
        </div>
      </div>

      <!-- 错误提示 -->
      <div v-if="error" class="bg-red-500/20 border border-red-600 rounded-lg p-4 mb-6 max-w-3xl mx-auto">
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-red-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-red-100 flex-1">{{ error }}</p>
          <button
            @click="retryLoadData"
            class="px-3 py-1 bg-red-600/50 hover:bg-red-600 rounded text-sm text-white transition-colors"
          >
            {{ t('common.retry') || '重试' }}
          </button>
        </div>
      </div>

      <!-- 页面标题 -->
      <div class="hidden md:block mb-8">
        <h1 class="text-3xl md:text-4xl font-bold mb-2 text-white">{{ t('music.homeH1') }}</h1>
        <p class="text-gray-400 text-lg">{{ t('music.discoverSubtitle') }}</p>
      </div>

      <!-- 音乐榜单部分 -->
      <div class="mb-4 lg:mb-12">
        <h2 class="text-2xl font-bold text-white mb-6">{{ t('music.musicTrending') }}</h2>

        <!-- PC网格布局，移动端隐藏 -->
        <div v-if="isLoadingTrending" class="hidden md:grid md:grid-cols-3 gap-4 lg:gap-6">
          <!-- Shimmer加载效果 -->
          <div v-for="i in 3" :key="i" class="bg-gray-800/60 backdrop-blur-sm rounded-xl overflow-hidden shadow-lg">
            <!-- 榜单标题 shimmer -->
            <div class="p-4 bg-gradient-to-r from-blue-600/30 to-purple-600/30 border-b border-gray-700">
              <div class="shimmer-title h-7 w-3/4 rounded-md"></div>
            </div>

            <!-- 榜单音乐列表 shimmer -->
            <div class="divide-y divide-gray-700/50">
              <div v-for="j in 3" :key="j" class="flex items-center p-3">
                <div class="w-8 text-center">
                  <div class="shimmer-text h-6 w-6 mx-auto rounded"></div>
                </div>
                <div class="shimmer-square w-12 h-12 rounded-md mr-3"></div>
                <div class="flex-1 min-w-0 mr-3">
                  <div class="shimmer-line h-4 w-3/4 rounded mb-2"></div>
                  <div class="shimmer-line h-3 w-1/2 rounded"></div>
                </div>
                <div class="w-10 h-10">
                  <div class="shimmer-circle w-10 h-10 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="hidden md:grid md:grid-cols-3 gap-4 lg:gap-6">
          <!-- 榜单卡片 -->
          <div v-for="trending in musicTrending" :key="trending.id" class="bg-gray-800/60 backdrop-blur-sm rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
            <!-- 榜单标题 -->
            <div class="p-4 bg-gradient-to-r from-blue-600/30 to-purple-600/30 border-b border-gray-700">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-xl font-bold text-white flex items-center">
                    {{ trending.name }}
                  </h3>
                </div>
                <NuxtLink
                  :to="getSectionById(trending.section_id, 'rank', trending.selected_option)
                    ? `/music/${getSectionById(trending.section_id, 'rank', trending.selected_option)?.path}`
                    : `/music?chart=${trending.section_id}&title=${trending.name}`"
                  :title="`${t('music.trendingSubtitle')}: ${trending.name}`"
                  :aria-label="`${t('music.trendingSubtitle')}: ${trending.name}`"
                  class="text-blue-400 cursor-pointer hover:text-blue-300 text-sm font-medium transition-colors">
                  {{ t('music.more') }}
                </NuxtLink>
              </div>
            </div>

            <!-- 榜单音乐列表 -->
            <div class="divide-y divide-gray-700/50">
              <div
                v-for="(music, index) in trending.top_songs"
                :key="music.id"
                class="flex items-center p-3 hover:bg-gray-700/30 transition-colors cursor-pointer"
              >
                <!-- 排名 -->
                <div class="w-8 text-center font-bold text-lg text-gray-400">{{ index + 1 }}</div>

                <!-- 封面 -->
                <div class="w-12 h-12 relative rounded-md overflow-hidden mr-3">
                  <CloudflareImg
                    :src="music.image_url"
                    :alt="music.title"
                    width="128"
                    height="128"
                    quality="60"
                    rounded="md"
                    loading="lazy"
                  />

                  <!-- 播放状态指示器 -->
                  <div v-if="isCurrentlyPlaying(music.id)" class="absolute inset-0 flex items-center justify-center bg-black/40 z-20">
                    <div class="sound-wave-small">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>

                <!-- 音乐信息 -->
                <div class="flex-1 min-w-0 mr-3" @click="navigateToMusicDetail(music)">
                  <h4 class="font-medium text-white truncate">{{ music.title }}</h4>
                  <p class="text-gray-400 text-sm truncate">{{ music.display_name }}</p>
                </div>
                <!-- 播放按钮 -->
                <button
                  @click.stop="playMusic(music)"
                  class="w-10 h-10 rounded-full flex items-center justify-center bg-gray-700/50 hover:bg-blue-600/50 transition-colors"
                >
                  <svg v-if="isCurrentlyPlaying(music.id)" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 移动端滑动布局，PC端隐藏 -->
        <div v-if="isLoadingTrending" class="md:hidden overflow-x-auto trending-scroll-container pb-4 relative">
          <div class="flex space-x-4 min-w-min">
            <!-- Shimmer效果 -->
            <div v-for="i in 3" :key="i" class="bg-gray-800/60 backdrop-blur-sm rounded-xl overflow-hidden shadow-lg flex-shrink-0 w-[85vw] max-w-[360px]">
              <!-- 榜单标题 shimmer -->
              <div class="p-4 bg-gradient-to-r from-blue-600/30 to-purple-600/30 border-b border-gray-700">
                <div class="shimmer-title h-7 w-3/4 rounded-md"></div>
              </div>

              <!-- 榜单音乐列表 shimmer -->
              <div class="divide-y divide-gray-700/50">
                <div v-for="j in 3" :key="j" class="flex items-center p-3">
                  <div class="w-8 text-center">
                    <div class="shimmer-text h-6 w-6 mx-auto rounded"></div>
                  </div>
                  <div class="shimmer-square w-12 h-12 rounded-md mr-3"></div>
                  <div class="flex-1 min-w-0 mr-3">
                    <div class="shimmer-line h-4 w-3/4 rounded mb-2"></div>
                    <div class="shimmer-line h-3 w-1/2 rounded"></div>
                  </div>
                  <div class="w-10 h-10">
                    <div class="shimmer-circle w-10 h-10 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="md:hidden overflow-x-auto trending-scroll-container pb-4 relative">
          <div class="flex space-x-4 min-w-min">
            <!-- 移动端榜单内容，优化为懒加载方式 -->
            <div
              v-for="trending in musicTrending"
              :key="trending.id"
              :data-ranking-id="trending.id"
              class="ranking-card bg-gray-800/60 backdrop-blur-sm rounded-xl overflow-hidden shadow-lg flex-shrink-0 w-[85vw] max-w-[360px]"
            >
              <!-- 榜单标题 -->
              <div class="p-4 bg-gradient-to-r from-blue-600/30 to-purple-600/30 border-b border-gray-700">
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="text-xl font-bold text-white flex items-center">
                      {{ trending.name }}
                    </h3>
                  </div>
                  <NuxtLink
                    :to="getSectionById(trending.section_id, 'rank', trending.selected_option)
                      ? `/music/${getSectionById(trending.section_id, 'rank', trending.selected_option)?.path}`
                      : `/music?chart=${trending.section_id}&title=${trending.name}`"
                    :title="`${t('music.trendingSubtitle')}: ${trending.name}`"
                    :aria-label="`${t('music.trendingSubtitle')}: ${trending.name}`"
                    class="text-blue-400 cursor-pointer hover:text-blue-300 text-sm font-medium transition-colors">
                    {{ t('music.more') }}
                  </NuxtLink>
                </div>
              </div>

              <!-- 榜单音乐列表 -->
              <div class="divide-y divide-gray-700/50">
                <!-- 所有榜单都立即渲染音乐列表内容 -->
                <div
                  v-for="(music, index) in trending.top_songs"
                  :key="music.id"
                  class="flex items-center p-3 hover:bg-gray-700/30 transition-colors cursor-pointer"
                >
                  <!-- 排名 -->
                  <div class="w-8 text-center font-bold text-lg text-gray-400">{{ index + 1 }}</div>

                  <!-- 封面：根据shouldLoadImages状态决定是否加载图片 -->
                  <div class="w-12 h-12 relative rounded-md overflow-hidden mr-3">
                    <template v-if="shouldLoadImages[trending.id]">
                      <CloudflareImg
                        :src="music.image_url"
                        :alt="music.title"
                        width="128"
                        height="128"
                        quality="60"
                        rounded="md"
                      />
                    </template>
                    <div v-else class="shimmer-square w-12 h-12 rounded-md absolute inset-0"></div>

                    <!-- 播放状态指示器 -->
                    <div v-if="isCurrentlyPlaying(music.id)" class="absolute inset-0 flex items-center justify-center bg-black/40 z-20">
                      <div class="sound-wave-small">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>

                  <!-- 音乐信息 -->
                  <div class="flex-1 min-w-0 mr-3" @click="navigateToMusicDetail(music)">
                    <h4 class="font-medium text-white truncate">{{ music.title }}</h4>
                    <p class="text-gray-400 text-sm truncate">{{ music.display_name }}</p>
                  </div>

                  <!-- 播放按钮 -->
                  <button
                    @click.stop="playMusic(music)"
                    :aria-label="!isCurrentlyPlaying(music.id) ? 'Play ' + music.title : 'Pause ' + music.title"
                    class="w-10 h-10 rounded-full flex items-center justify-center bg-gray-700/50 hover:bg-blue-600/50 transition-colors"
                  >
                    <svg v-if="isCurrentlyPlaying(music.id)" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 音乐分类部分 -->
      <div>
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold text-white">{{ t('music.musicCategories') }}</h2>
        </div>

        <!-- 移动端分类加载状态 -->
        <div v-if="isLoadingCategories" class="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6 md:gap-6">
          <!-- Shimmer 效果 -->
          <div
            v-for="i in 4"
            :key="i"
            class="relative rounded-xl overflow-hidden aspect-square shadow-lg bg-gray-800/40 group transition-all duration-300 hover:shadow-xl hover:scale-105"
          >
            <div class="shimmer-image absolute inset-0"></div>
            <!-- 渐变遮罩 -->
            <div class="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent z-20"></div>
            <!-- 分类信息 -->
            <div class="absolute bottom-0 left-0 right-0 p-4 z-30">
              <div class="shimmer-title h-6 w-3/4 rounded-md"></div>
            </div>
          </div>
        </div>

        <!-- 移动端分类内容 -->
        <div v-else class="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6 md:gap-6">
          <NuxtLink
            v-for="(category, index) in musicCategories"
            :key="category.id"
            :to="`/music/${category.search_tag}`"
            :title="`${t('music.exploreCategory')}: ${category.name}`"
            :aria-label="`${t('music.exploreCategory')}: ${category.name}`"
            :data-category-id="category.id"
            class="category-card group relative rounded-xl overflow-hidden aspect-square shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
          >
            <!-- 分类封面 -->
            <template v-if="shouldLoadCategoryImages[category.id]">
              <CloudflareImg
                :src="category.image_url"
                :alt="category.name"
                rounded="xl"
                :loading="index < 4 ? 'eager' : 'lazy'"
                :showPreload="false"
                width="128"
                height="128"
                quality="80"
                class="transition-transform duration-500 group-hover:scale-110"
              />
            </template>
            <template v-else>
              <div class="shimmer-image absolute inset-0"></div>
            </template>

            <!-- 渐变遮罩 -->
            <div class="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent z-20"></div>

            <!-- 分类信息 -->
            <div class="absolute bottom-0 left-0 right-0 p-4 z-30">
              <h3 class="text-xl font-bold text-white mb-1 group-hover:text-blue-300 transition-colors">
                {{ category.name }}
              </h3>
            </div>
          </NuxtLink>
        </div>
      </div>

      <!-- Popular AI Music Generators -->
      <div class="mt-8">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold text-white">Popular AI Music Generators</h2>
          <NuxtLink to="/discover" class="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors">
            View all →
          </NuxtLink>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <NuxtLink to="/discover/ai-birthday-song-generator" class="px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-gray-200 text-sm hover:border-blue-500/50 hover:text-white transition-all text-center">Birthday Song</NuxtLink>
          <NuxtLink to="/discover/ai-love-song-generator" class="px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-gray-200 text-sm hover:border-blue-500/50 hover:text-white transition-all text-center">Love Song</NuxtLink>
          <NuxtLink to="/discover/ai-rap-song-generator" class="px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-gray-200 text-sm hover:border-blue-500/50 hover:text-white transition-all text-center">Rap & Hip-Hop</NuxtLink>
          <NuxtLink to="/discover/ai-pop-song-generator" class="px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-gray-200 text-sm hover:border-blue-500/50 hover:text-white transition-all text-center">Pop Song</NuxtLink>
          <NuxtLink to="/discover/ai-anime-song-generator" class="px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-gray-200 text-sm hover:border-blue-500/50 hover:text-white transition-all text-center">Anime & J-Pop</NuxtLink>
          <NuxtLink to="/discover/youtube-intro-music-generator" class="px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-gray-200 text-sm hover:border-blue-500/50 hover:text-white transition-all text-center">YouTube Intro</NuxtLink>
          <NuxtLink to="/discover/podcast-intro-music-generator" class="px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-gray-200 text-sm hover:border-blue-500/50 hover:text-white transition-all text-center">Podcast Music</NuxtLink>
          <NuxtLink to="/discover/royalty-free-ai-music-generator" class="px-4 py-3 rounded-xl bg-gray-800/50 border border-gray-700/50 text-gray-200 text-sm hover:border-blue-500/50 hover:text-white transition-all text-center">Royalty-Free</NuxtLink>
        </div>
      </div>

      <!-- SEO Content Sections: Features & FAQ -->
      <div class="mt-8 lg:mt-16 mb-12 space-y-12 lg:space-y-24">
        <!-- Features Section -->
        <section>
          <div class="text-center mb-10">
            <h2 class="text-3xl font-bold text-white mb-4">{{ t('music.sections.features') }}</h2>
            <div class="w-20 h-1 bg-blue-500 mx-auto rounded-full"></div>
          </div>

          <div class="grid md:grid-cols-3 gap-6">
            <div class="bg-gray-800/40 p-6 rounded-xl border border-gray-700/50 hover:border-blue-500/30 transition-colors duration-300">
              <h3 class="text-xl font-semibold text-blue-400 mb-3">{{ t('music.featureList.textToMusic.title') }}</h3>
              <p class="text-gray-300 leading-relaxed">{{ t('music.featureList.textToMusic.desc') }}</p>
            </div>
            <div class="bg-gray-800/40 p-6 rounded-xl border border-gray-700/50 hover:border-purple-500/30 transition-colors duration-300">
              <h3 class="text-xl font-semibold text-purple-400 mb-3">{{ t('music.featureList.lyricsGenerator.title') }}</h3>
              <p class="text-gray-300 leading-relaxed">{{ t('music.featureList.lyricsGenerator.desc') }}</p>
            </div>
            <div class="bg-gray-800/40 p-6 rounded-xl border border-gray-700/50 hover:border-teal-500/30 transition-colors duration-300">
              <h3 class="text-xl font-semibold text-teal-400 mb-3">{{ t('music.featureList.vocalSeparation.title') }}</h3>
              <p class="text-gray-300 leading-relaxed">{{ t('music.featureList.vocalSeparation.desc') }}</p>
            </div>
          </div>
        </section>

        <!-- FAQ Section -->
        <section>
          <div class="text-center mb-10">
            <h2 class="text-3xl font-bold text-white mb-4">{{ t('music.sections.faq') }}</h2>
            <p class="text-gray-400 text-lg leading-relaxed mb-6">{{ t('music.sections.faqSubtitle') }}</p>
             <div class="w-20 h-1 bg-purple-500 mx-auto rounded-full"></div>
          </div>

          <div class="grid md:grid-cols-3 gap-6">
            <div
              v-for="i in 3"
              :key="i"
              class="bg-gray-800/40 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden transition-all duration-300 hover:border-blue-500/30 h-fit"
            >
              <button
                @click="toggleFaq(i)"
                class="w-full flex items-center justify-between p-5 text-left focus:outline-none group"
              >
                <h3 class="font-semibold text-lg text-white group-hover:text-blue-300 transition-colors">{{ t(`music.faqList.q${i}`) }}</h3>
                <span
                  class="transform transition-transform duration-300 text-gray-400 group-hover:text-blue-300"
                  :class="{ 'rotate-180': activeFaqIndex === i }"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </span>
              </button>
              <div
                v-show="activeFaqIndex === i"
                class="px-5 pb-5 text-gray-300 border-t border-gray-700/30 pt-3"
              >
                <p class="leading-relaxed">{{ t(`music.faqList.a${i}`) }}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 添加shimmer效果样式 */
.shimmer-title,
.shimmer-line,
.shimmer-circle,
.shimmer-square,
.shimmer-text,
.shimmer-image {
  @apply bg-gradient-to-r from-gray-700/50 via-gray-600/50 to-gray-700/50 bg-[length:400%_100%];
  animation: shimmer 2s infinite linear;
}

@keyframes shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

/* 音浪动画 */
.sound-wave {
  display: flex;
  align-items: center;
  height: 20px;
  gap: 2px;
}

.sound-wave span {
  display: block;
  width: 3px;
  height: 100%;
  background-color: white;
  border-radius: 3px;
  animation: sound-wave-animation 1.2s infinite ease-in-out;
}

.sound-wave span:nth-child(1) {
  animation-delay: 0s;
}

.sound-wave span:nth-child(2) {
  animation-delay: 0.3s;
}

.sound-wave span:nth-child(3) {
  animation-delay: 0.6s;
}

.sound-wave span:nth-child(4) {
  animation-delay: 0.9s;
}

.sound-wave-small {
  display: flex;
  align-items: center;
  height: 14px;
  gap: 2px;
}

.sound-wave-small span {
  display: block;
  width: 2px;
  height: 100%;
  background-color: white;
  border-radius: 2px;
  animation: sound-wave-animation 1.2s infinite ease-in-out;
}

.sound-wave-small span:nth-child(1) {
  animation-delay: 0s;
}

.sound-wave-small span:nth-child(2) {
  animation-delay: 0.3s;
}

.sound-wave-small span:nth-child(3) {
  animation-delay: 0.6s;
}

@keyframes sound-wave-animation {
  0%, 100% {
    height: 30%;
  }
  50% {
    height: 100%;
  }
}

/* 隐藏滚动条但保留功能 */
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;  /* Chrome, Safari and Opera */
}

/* 横向滑动容器样式 */
.trending-scroll-container {
  -webkit-overflow-scrolling: touch; /* 提升iOS滑动体验 */
  scrollbar-width: none; /* 隐藏Firefox滚动条 */
  -ms-overflow-style: none; /* 隐藏IE滚动条 */
  scroll-snap-type: x mandatory; /* 启用滚动捕捉 */
  padding-bottom: 16px; /* 为可能的滚动条预留空间 */
}

/* 隐藏WebKit浏览器的滚动条 */
.trending-scroll-container::-webkit-scrollbar {
  display: none;
}

/* 滚动捕捉 */
.trending-scroll-container > div > div {
  scroll-snap-align: start; /* 滚动时对齐到开始位置 */
  scroll-snap-stop: always; /* 总是停在捕捉点 */
}

/* 小屏幕音浪动画 */
.sound-wave-small {
  display: flex;
  align-items: center;
  height: 12px;
  gap: 1px;
}

.sound-wave-small span {
  display: block;
  width: 2px;
  height: 8px;
  background-color: white;
  border-radius: 1px;
  animation: sound-wave 1.2s infinite ease-in-out;
}

.sound-wave-small span:nth-child(2) {
  animation-delay: 0.2s;
}

.sound-wave-small span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes sound-wave {
  0%, 100% {
    transform: scaleY(0.5);
  }
  50% {
    transform: scaleY(1);
  }
}
</style>
